import { apiFetch, authHeader, jsonBody } from '../../shared/api/httpClient';
import type { MeResponse } from '../../entities/user/types';
import { clientHeaders } from '../../shared/lib/device';

export type ResponsibleGamingLimits = {
  daily_bet_limit_brl?: number;
  weekly_bet_limit_brl?: number;
  monthly_bet_limit_brl?: number;
  pending_daily_bet_limit_brl?: number | null;
  pending_daily_bet_limit_apply_at?: number | null;
  pending_weekly_bet_limit_brl?: number | null;
  pending_weekly_bet_limit_apply_at?: number | null;
  pending_monthly_bet_limit_brl?: number | null;
  pending_monthly_bet_limit_apply_at?: number | null;
  daily_bet_used_brl?: number;
  daily_bet_remaining_brl?: number;
  weekly_bet_used_brl?: number;
  weekly_bet_remaining_brl?: number;
  monthly_bet_used_brl?: number;
  monthly_bet_remaining_brl?: number;
  cool_off_until?: number;
  self_excluded?: boolean;
};

export type ResponsibleGamingResponse = {
  limits?: ResponsibleGamingLimits;
};

export type ActivityDrumItem = {
  id: string;
  type?: string;
  layout?: string;
  tier?: number;
  priority?: number;
  starts_at?: number | null;
  expires_at?: number | null;
  icon?: string;
  lifecycle?: string;
  cta_action?: string;
  // Which ACTIVITY_CATALOG entry this card supersedes, so the same activity is
  // never listed twice — once rich, once as a bare name.
  catalog_code?: string;
  // Which band of the Ways-to-Earn list this card belongs to. Cadence, not
  // type: a player reasons in "what can I do today" vs "what am I building
  // towards" vs "what do I do once and never again".
  group?: 'today' | 'progress' | 'once';
  // Feed band, computed from live state: 0 claim-now, 1 open-today,
  // 2 in-progress, 3 to-start, 4 done-for-today. Order carries the message.
  band?: number;
  // Payoff modifiers: a dimmed figure with a prefix is "tomorrow's" — a
  // comeback hook, never gold. A note is a micro-qualifier ("por amigo").
  reward_prefix?: string;
  reward_dim?: boolean;
  reward_note?: string;
  // Composed client-side: the figure inside the ask that deserves the card's
  // accent (the raffle card's ticket count), and a draw to count down to —
  // rendered live, per second, by the card itself.
  ask_accent?: string;
  countdown_at?: number;
  countdown_variant?: 'long' | 'short';
  // Labeled progress meters (the VIP card's bets/deposits pair) — absolute
  // figures, both from live account state, never a bare percentage.
  meters?: Array<{ label: string; have: number; target: number }>;
  // What the player GETS by doing this, when the activity is not a ladder and
  // has no progress to show (binding a contact). Reasons, not steps.
  perks?: string[];
  // This card's rewards include raffle tickets — the raffle card's
  // zero-ticket CTA scrolls to the first card that has this set.
  pays_tickets?: boolean;
  // Composed client-side, never sent by the backend: the rungs of a laddered
  // activity (1st→5th deposit, 5→10 rounds, invite tiers) plus where the
  // player stands on it.
  steps?: Array<{ label: string; reward: string; state: 'done' | 'now' | 'up'; fill?: number }>;
  cta_label?: string;
  cta_label_i18n?: string;
  color_theme?: string;
  status?: string;
  reward?: string;
  reward_label?: string;
  progress_label?: string;
  event_id?: string;
  eventId?: string;
  raffle_event_id?: string;
  raffleEventId?: string;
  progress_pct?: number;
  title?: string;
  title_i18n?: string;
  title_params?: Record<string, string | number>;
  title_main?: string;
  title_main_i18n?: string;
  title_main_params?: Record<string, string | number>;
  secondary?: string;
  secondary_i18n?: string;
  secondary_params?: Record<string, string | number>;
  pool_label?: string;
  countdown_seconds?: number;
  heat_tone_seconds?: number;
  draw_at?: number;
  register_close_at?: number;
  entry_count?: number;
  min_entries_to_run?: number;
  entry_limit?: number;
  my_ticket_balance?: number;
  my_entry_count?: number;
  my_prize_brl?: number;
  state?: Record<string, unknown>;
};

export type ActivityDrumResponse = {
  items?: ActivityDrumItem[];
  server_time?: number | { unix?: number };
};

export type ActivityCheckin = {
  track: 'login' | 'bet' | 'deposit' | string;
  businessDate?: string;
  eligibility?: {
    eligible?: boolean;
    track?: string;
    businessDate?: string;
  };
  claimed?: boolean;
  streakDay?: number;
  active?: boolean;
  configType?: string;
};

export type ActivityAudit = {
  id: number | string;
  sourceType?: string;
  sourceLabel?: string;
  currency?: string;
  bonusAmount?: number;
  auditMultiplier?: number;
  requiredAmount?: number;
  completedAmount?: number;
  remainingAmount?: number;
  status?: string;
  createdAt?: number;
  completedAt?: number | null;
  activityType?: string;
  triggerType?: string;
  businessDate?: string;
};

export type ActivityClaim = {
  id: number | string;
  activityType?: string;
  track?: string;
  businessDate?: string;
  streakDay?: number;
  status?: string;
  rewardGrantId?: number | null;
  createdAt?: number;
};

export type CheckinDay = { day: number; amount?: number; kind?: 'cash' | 'coupon' | 'raffle_ticket' | string; ticket_count?: number; coupon_code?: string; coupon_bonus_type?: string; coupon_bonus_value?: number };
export type CheckinTrackConfig = { track: string; days: CheckinDay[] };

export type ActivityConfig = {
  activityType?: string;
  enabled?: boolean;
  titleI18n?: Record<string, string>;
  descriptionI18n?: Record<string, string>;
  claimTextI18n?: Record<string, string>;
  auditMultiplier?: number;
  minTriggerAmount?: number;
  maxBonusPerGrant?: number;
  config?: {
    reset_on_missed_day?: boolean;
    tracks?: CheckinTrackConfig[];
    [key: string]: unknown;
  };
};

export type ActivityDailyBet = {
  tiers?: Array<{ rounds?: number; amount?: number; reached?: boolean; paid?: boolean }>;
  qualifiedRounds?: number;
  minSingleBetAmount?: number;
  businessDate?: string;
};

// How many players this user has brought in — today and lifetime.
// total counts registrations; converted counts friends who actually made a
// first deposit — the count the reward brackets are indexed by. earned is the
// money already granted, read from the grant ledger.
export type ActivityReferrals = { today?: number; total?: number; converted?: number; earned?: number };

export type ActivitiesSummaryResponse = {
  country?: string;
  businessDate?: string;
  configs?: ActivityConfig[];
  checkins?: ActivityCheckin[];
  dailyBet?: ActivityDailyBet;
  referrals?: ActivityReferrals;
  // Completed deposits — which rung of the deposit-bonus ladder the player is on.
  depositCount?: number;
  audits?: ActivityAudit[];
  claims?: ActivityClaim[];
};

export type ActivitiesAuditsResponse = {
  summary?: {
    activeCount?: number;
    remainingAmount?: number;
  };
  items?: ActivityAudit[];
};

export type ActivityClaimResponse = {
  claimed?: boolean;
  idempotent?: boolean;
  item?: ActivityClaim;
  reward?: { rewardKind?: string; amount?: number; ticketCount?: number; couponCode?: string };
};

export type ActivityDrumStateResponse = {
  ok?: boolean;
  state?: Record<string, unknown>;
};

export type SecuritySession = {
  jti: string;
  current?: boolean;
  active?: boolean;
  ip?: string;
  user_agent?: string;
  created_at?: number;
  expires_at?: number;
  last_seen_at?: number;
};

export type SecuritySessionsResponse = {
  sessions?: SecuritySession[];
};

export type ContactOtpResponse = {
  expires_in?: number;
  code_expires_in_s?: number;
  retry_after_ms?: number;
};

export type ContactConfirmResponse = MeResponse & {
  reward_granted?: boolean;
  channel?: 'phone' | 'email';
};

export function fetchResponsibleGaming(token: string) {
  return apiFetch<ResponsibleGamingResponse>('/api/responsible-gaming', {
    cache: 'no-store',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

export function updateResponsibleGaming(
  token: string,
  payload: { dailyBetLimitBrl?: number; weeklyBetLimitBrl?: number; monthlyBetLimitBrl?: number; coolOffDays?: number },
) {
  return apiFetch<ResponsibleGamingResponse>('/api/responsible-gaming', {
    method: 'PATCH',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: jsonBody(payload),
  });
}

export function fetchActivityDrum(token: string) {
  return apiFetch<ActivityDrumResponse>('/api/activity-drum', {
    cache: 'no-store',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

export function fetchActivitiesSummary(token: string) {
  return apiFetch<ActivitiesSummaryResponse>('/api/activities', {
    cache: 'no-store',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

export function fetchActivityAudits(token: string, limit = 20) {
  return apiFetch<ActivitiesAuditsResponse>(`/api/activities/audits?limit=${encodeURIComponent(String(limit))}`, {
    cache: 'no-store',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

export function claimActivityCheckin(token: string, track: string) {
  return apiFetch<ActivityClaimResponse>(`/api/activities/checkins/${encodeURIComponent(track)}/claim`, {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

export type RewardInboxItem = {
  id: number;
  activityType: string;
  status: 'in_progress' | 'claimable' | 'claimed' | string;
  rewardKind: 'cash' | 'coupon' | 'raffle_ticket' | 'bundle' | string;
  cashAmount?: number;
  auditMultiplier?: number;
  ticketCount?: number;
  couponTemplateId?: number | null;
  progressCurrent?: number;
  progressTarget?: number | null;
  progressUnit?: string | null;
  titleI18n?: Record<string, string>;
  descI18n?: Record<string, string>;
  availableAt?: number | null;
  expiresAt?: number | null;
  claimedAt?: number | null;
};
export type RewardInboxResponse = {
  inProgress?: RewardInboxItem[];
  claimable?: RewardInboxItem[];
  recentClaimed?: RewardInboxItem[];
  summary?: { claimableCount?: number; claimableCashTotal?: number };
};

export function fetchRewardInbox(token: string) {
  return apiFetch<RewardInboxResponse>('/api/rewards', {
    cache: 'no-store',
    headers: { ...clientHeaders(), ...authHeader(token) },
  });
}

export function claimReward(token: string, id: number) {
  return apiFetch<{ item?: RewardInboxItem }>(`/api/rewards/${id}/claim`, {
    method: 'POST',
    headers: { ...clientHeaders(), ...authHeader(token) },
  });
}

export function updateActivityDrumState(token: string, activityId: string, action: 'view' | 'click' | 'dismiss') {
  return apiFetch<ActivityDrumStateResponse>(`/api/activity-drum/${encodeURIComponent(activityId)}/state`, {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: jsonBody({ action }),
  });
}

export function fetchSecuritySessions(token: string) {
  return apiFetch<SecuritySessionsResponse>('/api/me/sessions', {
    cache: 'no-store',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

export function revokeSecuritySession(token: string, jti: string) {
  return apiFetch<SecuritySessionsResponse>(`/api/me/sessions/${encodeURIComponent(jti)}`, {
    method: 'DELETE',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

export function sendContactOtp(token: string, payload: { phone?: string; email?: string }) {
  return apiFetch<ContactOtpResponse>('/api/me/contact/send-otp', {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: jsonBody(payload),
  });
}

export function confirmContactOtp(token: string, payload: { phone?: string; email?: string; otp: string }) {
  return apiFetch<ContactConfirmResponse>('/api/me/contact/confirm', {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: jsonBody(payload),
  });
}
